/**
 * Centralized Error Reporting Service
 *
 * This service handles error logging, reporting, and tracking.
 * It can be configured to send errors to external services like Sentry,
 * LogRocket, or a custom backend endpoint.
 */

import { logger } from '@/utils/logger';
import { createErrorReport, ErrorReport, ErrorSeverity } from '@/types/errors';

// Environment detection - works with both Vite and standard Node.js
const isDev = typeof process !== 'undefined'
  ? process.env?.NODE_ENV === 'development'
  : false;

// Configuration for error reporting
interface ErrorReportingConfig {
  /** Whether to log errors to console */
  logToConsole: boolean;
  /** Whether to send errors to backend */
  sendToBackend: boolean;
  /** Backend endpoint for error reporting */
  backendEndpoint?: string;
  /** Maximum number of errors to queue before sending */
  batchSize: number;
  /** Interval in ms to flush error queue */
  flushInterval: number;
  /** Whether to include user info in reports */
  includeUserInfo: boolean;
  /** Environment (development, staging, production) */
  environment: string;
}

// Default configuration
const defaultConfig: ErrorReportingConfig = {
  logToConsole: true,
  sendToBackend: !isDev,
  backendEndpoint: '/api/v1/errors',
  batchSize: 10,
  flushInterval: 30000, // 30 seconds
  includeUserInfo: true,
  environment: isDev ? 'development' : 'production',
};

// Error queue for batching
let errorQueue: ErrorReport[] = [];
let flushTimeout: NodeJS.Timeout | null = null;
let config: ErrorReportingConfig = { ...defaultConfig };

// Track reported errors to prevent duplicates
const reportedErrors = new Map<string, number>();
const ERROR_DEDUP_WINDOW_MS = 60000; // 1 minute

/**
 * Initialize error reporting with custom configuration
 */
export function initErrorReporting(customConfig: Partial<ErrorReportingConfig> = {}): void {
  config = { ...defaultConfig, ...customConfig };

  // Start flush interval if sending to backend
  if (config.sendToBackend && config.flushInterval > 0) {
    startFlushInterval();
  }

  logger.info('Error reporting initialized', { environment: config.environment });
}

/**
 * Start the periodic flush interval
 */
function startFlushInterval(): void {
  if (flushTimeout) {
    clearInterval(flushTimeout);
  }

  flushTimeout = setInterval(() => {
    flushErrorQueue();
  }, config.flushInterval);
}

/**
 * Generate a unique key for an error to detect duplicates
 */
function getErrorKey(error: Error, componentStack?: string): string {
  const stack = error.stack?.split('\n').slice(0, 3).join('') || '';
  return `${error.name}:${error.message}:${stack}:${componentStack?.slice(0, 100) || ''}`;
}

/**
 * Check if an error has been recently reported
 */
function isDuplicateError(errorKey: string): boolean {
  const now = Date.now();
  const lastReported = reportedErrors.get(errorKey);

  if (lastReported && now - lastReported < ERROR_DEDUP_WINDOW_MS) {
    return true;
  }

  // Cleanup old entries
  for (const [key, timestamp] of reportedErrors.entries()) {
    if (now - timestamp > ERROR_DEDUP_WINDOW_MS) {
      reportedErrors.delete(key);
    }
  }

  reportedErrors.set(errorKey, now);
  return false;
}

/**
 * Get current user ID if available
 */
function getCurrentUserId(): string | undefined {
  if (!config.includeUserInfo) {
    return undefined;
  }

  try {
    // Try to get user from auth store
    const authData = localStorage.getItem('auth-storage');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed?.state?.user?.id;
    }
  } catch {
    // Ignore errors when getting user ID
  }

  return undefined;
}

/**
 * Report an error from an error boundary
 */
export function reportBoundaryError(
  error: Error,
  errorInfo: React.ErrorInfo,
  boundaryName: string = 'ErrorBoundary',
  additionalContext: Record<string, unknown> = {}
): void {
  const errorKey = getErrorKey(error, errorInfo.componentStack || undefined);

  // Skip duplicate errors
  if (isDuplicateError(errorKey)) {
    logger.debug('Skipping duplicate error report', { error: error.message });
    return;
  }

  const userId = getCurrentUserId();
  const report = createErrorReport(error, errorInfo.componentStack || undefined, userId);

  // Add boundary-specific context
  const enrichedReport: ErrorReport & { boundaryContext: Record<string, unknown> } = {
    ...report,
    boundaryContext: {
      boundaryName,
      ...additionalContext,
      reactInfo: {
        componentStack: errorInfo.componentStack,
      },
    },
  };

  // Log to console
  if (config.logToConsole) {
    logger.error(`[${boundaryName}] Caught error:`, error);
    logger.error('Component stack:', errorInfo.componentStack);

    if (isDev) {
      console.group(`Error Boundary: ${boundaryName}`);
      console.error('Error:', error);
      console.error('Component Stack:', errorInfo.componentStack);
      console.log('Report:', enrichedReport);
      console.groupEnd();
    }
  }

  // Queue for backend reporting
  if (config.sendToBackend) {
    queueError(enrichedReport);
  }

  // For critical errors, flush immediately
  if (report.severity === ErrorSeverity.CRITICAL) {
    flushErrorQueue();
  }
}

/**
 * Report a generic error (not from boundary)
 */
export function reportError(
  error: Error | unknown,
  context: Record<string, unknown> = {}
): void {
  const actualError = error instanceof Error ? error : new Error(String(error));
  const errorKey = getErrorKey(actualError);

  // Skip duplicate errors
  if (isDuplicateError(errorKey)) {
    return;
  }

  const userId = getCurrentUserId();
  const report = createErrorReport(actualError, undefined, userId);

  const enrichedReport = {
    ...report,
    additionalContext: context,
  };

  if (config.logToConsole) {
    logger.error('Error reported:', actualError);
  }

  if (config.sendToBackend) {
    queueError(enrichedReport);
  }
}

/**
 * Queue an error for batch sending
 */
function queueError(report: ErrorReport): void {
  errorQueue.push(report);

  // Flush if queue is full
  if (errorQueue.length >= config.batchSize) {
    flushErrorQueue();
  }
}

/**
 * Flush the error queue to the backend
 */
async function flushErrorQueue(): Promise<void> {
  if (errorQueue.length === 0) {
    return;
  }

  const errors = [...errorQueue];
  errorQueue = [];

  if (!config.backendEndpoint) {
    return;
  }

  try {
    // Use sendBeacon for reliability (works even if page is closing)
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify({ errors, environment: config.environment })], {
        type: 'application/json',
      });
      const sent = navigator.sendBeacon(config.backendEndpoint, blob);

      if (!sent) {
        // Fallback to fetch
        await sendViaFetch(errors);
      }
    } else {
      await sendViaFetch(errors);
    }

    logger.debug(`Flushed ${errors.length} errors to backend`);
  } catch (err) {
    // Re-queue errors on failure (with limit to prevent infinite growth)
    if (errorQueue.length < config.batchSize * 3) {
      errorQueue = [...errors, ...errorQueue];
    }
    logger.warn('Failed to flush error queue:', err);
  }
}

/**
 * Send errors via fetch API
 */
async function sendViaFetch(errors: ErrorReport[]): Promise<void> {
  if (!config.backendEndpoint) return;

  await fetch(config.backendEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ errors, environment: config.environment }),
    credentials: 'include',
    keepalive: true,
  });
}

/**
 * Create a breadcrumb for tracking user actions
 */
export interface Breadcrumb {
  type: 'navigation' | 'click' | 'input' | 'api' | 'console' | 'custom';
  category: string;
  message: string;
  data?: Record<string, unknown>;
  timestamp: Date;
}

const breadcrumbs: Breadcrumb[] = [];
const MAX_BREADCRUMBS = 50;

/**
 * Add a breadcrumb for debugging
 */
export function addBreadcrumb(breadcrumb: Omit<Breadcrumb, 'timestamp'>): void {
  breadcrumbs.push({
    ...breadcrumb,
    timestamp: new Date(),
  });

  // Keep only recent breadcrumbs
  while (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.shift();
  }
}

/**
 * Get recent breadcrumbs for error context
 */
export function getBreadcrumbs(): Breadcrumb[] {
  return [...breadcrumbs];
}

/**
 * Clear all breadcrumbs
 */
export function clearBreadcrumbs(): void {
  breadcrumbs.length = 0;
}

/**
 * Cleanup on page unload
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (errorQueue.length > 0) {
      flushErrorQueue();
    }
  });

  // Also handle visibilitychange for mobile
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && errorQueue.length > 0) {
      flushErrorQueue();
    }
  });
}

// Export singleton instance methods
export const errorReporting = {
  init: initErrorReporting,
  reportBoundaryError,
  reportError,
  addBreadcrumb,
  getBreadcrumbs,
  clearBreadcrumbs,
  flush: flushErrorQueue,
};

export default errorReporting;
